import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useInView, useReducedMotion } from "framer-motion";
import { bailSteps } from "../../data/home";
import { SectionHeading } from "../ui/SectionHeading";
import { MagneticCallButton } from "../ui/MagneticCallButton";

// Half of the max inter-step gap, so adjacent steps sit clamp(1.5rem, 3vw, 2.5rem) apart
const STEP_PAD = "clamp(0.75rem, 1.5vw, 1.25rem)";
const CIRCLE_HALF = "24px";
export function HowBailWorks() {
  const [active, setActive] = useState(0);
  const reduce = useReducedMotion();
  return <section aria-labelledby="how-title" className="gutter bg-white py-[clamp(4rem,7vw,6rem)]" data-id="element-227" data-mp="1kk0xnlzv13fw">
      <div className="grid gap-[clamp(2.5rem,5vw,6rem)] lg:grid-cols-[0.9fr_1.1fr] ultra:grid-cols-[0.8fr_1.2fr]" data-id="element-228" data-mp="1fkxu65zj35a1">
        <div className="lg:sticky lg:top-28 lg:h-fit" data-id="element-229" data-mp="2d0twnjrumkbb">
          <SectionHeading id="how-title" eyebrow="How bail works with us" title={<>
                Four calm steps <em className="text-gold-hover" data-id="element-231" data-mp="11r53qgu3q8ap">home.</em>
              </>} intro="No jargon, no pressure. Here's exactly what happens from your first call to the moment your loved one walks out." data-id="element-230" data-mp="2d2epn4yilv72" />
          <div className="relative mt-8 hidden h-[clamp(6rem,9vw,10rem)] lg:block" aria-hidden="true" data-id="element-232" data-mp="s127m7rooe85">
            <AnimatePresence mode="wait" initial={false} data-id="element-233" data-mp="wyu2nanmkggk">
              <motion.div key={active} initial={reduce ? false : {
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={reduce ? undefined : {
              opacity: 0,
              y: -12
            }} transition={{
              duration: 0.25,
              ease: [0.22, 1, 0.36, 1]
            }} className="absolute inset-0 flex items-end gap-5" data-id="element-234" data-mp="i34lpqxaqtip">
                <span className="font-serif text-[clamp(6rem,9vw,10rem)] font-light leading-[0.8] text-navy" data-id="element-235" data-mp="23g7gxumblb0q">
                  {String(active + 1).padStart(2, "0")}
                </span>
                <span className="mb-2 font-serif text-2xl italic text-gold-hover" data-id="element-236" data-mp="260x37k4hxrvu">{bailSteps[active].title}</span>
              </motion.div>
            </AnimatePresence>
          </div>
          <div className="mt-8 hidden lg:block" data-id="element-237" data-mp="1x0gzc9juciqg">
            <MagneticCallButton data-id="element-238" data-mp="1x19x4l0tnxwg" />
          </div>
        </div>

        <ol className="relative lg:self-center" data-id="element-239" data-mp="1s1nlgg0n6ppr">
          {bailSteps.map((s, i) => <Step key={s.title} index={i} title={s.title} body={s.body} active={active === i} filled={i < active} isLast={i === bailSteps.length - 1} onActive={setActive} data-id="element-240" data-mp="2pp620d0jow4b" data-mpi={i} />)}
        </ol>
      </div>
    </section>;
}
type StepProps = {
  index: number;
  title: string;
  body: string;
  active: boolean;
  filled: boolean;
  isLast: boolean;
  onActive: (i: number) => void;
};
function Step({
  index,
  title,
  body,
  active,
  filled,
  isLast,
  onActive
}: StepProps) {
  const ref = useRef<HTMLLIElement>(null);
  const inView = useInView(ref, {
    margin: "-45% 0px -45% 0px"
  });
  useEffect(() => {
    if (inView) onActive(index);
  }, [inView, index, onActive]);
  return <li ref={ref} className="relative flex items-start gap-6 sm:gap-10" style={{
    paddingBlock: STEP_PAD
  }} data-id="element-241" data-mp="3kyj43ikpba5l">
      {/* Connector to the next circle — the line runs only from step 1 to step 4 */}
      {!isLast && <span className="absolute left-[23px] w-px bg-navy/10" style={{
      top: `calc(${STEP_PAD} + ${CIRCLE_HALF})`,
      height: "100%"
    }} aria-hidden="true" data-id="element-242" data-mp="3tzpnbzeqiz2j">
          <motion.span className="absolute inset-0 origin-top bg-gold" initial={false} animate={{
        scaleY: filled ? 1 : 0
      }} transition={{
        duration: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-243" data-mp="7itmbc924lco" />
        </span>}
      <span className={`relative z-10 grid h-12 w-12 shrink-0 place-items-center rounded-full border font-serif text-lg transition-colors duration-300 ease-out ${active || filled ? "border-gold bg-navy text-gold-soft" : "border-navy/15 bg-white text-navy"}`} aria-hidden="true" data-id="element-244" data-mp="hfpu1fr4t3g6">
        {index + 1}
      </span>
      <div className="pt-1" data-id="element-245" data-mp="1rz9iabz3gr0s">
        <p className="eyebrow text-gold-hover lg:hidden" data-id="element-246" data-mp="1rzjckbr69kca">Step {String(index + 1).padStart(2, "0")}</p>
        <h3 className="mt-1 font-serif text-[clamp(1.5rem,2vw,2.25rem)] leading-tight text-navy lg:mt-0" data-id="element-247" data-mp="1o0tzyldyk6tp">{title}</h3>
        <p className="measure mt-2 max-w-[52ch] text-lg text-muted" data-id="element-248" data-mp="10azl5ya2w86t">{body}</p>
      </div>
    </li>;
}